import { useState, useEffect } from 'react';
import { Search, Filter, Grid3x3, List, FileText, Calendar, Download, Eye, MoreVertical, ChevronLeft, ChevronRight, ChevronDown, Plus, Trash2, ExternalLink, Loader2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import Markdown from 'markdown-to-jsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { documentApi, SessionDocument } from '@/services/api';
import { useNotificationStore } from '@/stores';
import { useJobPollingStore } from '@/stores/jobPollingStore';
import { useCompactMode } from '@/hooks/useCompactMode';
import { addLoadingDocument, removeLoadingDocument } from '@/utils/loadingDocuments';

interface DocumentsPageProps {
  onDocumentSelect: (doc: any) => void;
  isDocumentViewerOpen?: boolean;
  onAddDocuments?: () => void;
}

export function DocumentsPage({ onDocumentSelect, isDocumentViewerOpen = false, onAddDocuments }: DocumentsPageProps) {
  // Fix adjacent bold/italic markers that confuse markdown parsers
  const fixMarkdownFormatting = (content: string): string => {
    if (!content) return content;

    // Replace **text***italic* pattern with proper HTML, preserving what comes after
    // Match: **bold***italic*[anything after]
    // This handles cases like **28***a*,**28***b*and
    let fixed = content.replace(/\*\*([^*]+?)\*\*\*([^*]+?)\*([,;:.\s])?/g, (match, bold, italic, after) => {
      // If there's punctuation or space after, preserve it; otherwise add a space before letters
      const suffix = after || '';
      return `<b>${bold}</b><i>${italic}</i>${suffix}`;
    });

    // Handle cases where italic is followed immediately by a letter (like *b*and)
    fixed = fixed.replace(/<\/i>([a-zA-Z])/g, '</i> $1');

    // Also handle *italic***bold* pattern
    fixed = fixed.replace(/\*([^*]+?)\*\*\*([^*]+?)\*\*/g, '<i>$1</i><b>$2</b>');

    return fixed;
  };

  // Convert markdown to HTML (for use in previews)
  const markdownToHtml = (content: string): string => {
    if (!content) return content;

    // First fix problematic adjacent markers
    let html = fixMarkdownFormatting(content);

    // Convert remaining bold **text**
    html = html.replace(/\*\*([^*]+?)\*\*/g, '<b>$1</b>');

    // Convert remaining italic *text*
    html = html.replace(/\*([^*]+?)\*/g, '<i>$1</i>');

    return html;
  };

  // Format resource ID for display (e.g., "patent-9864572" -> "US Patent 9864572")
  const formatResourceId = (resourceId: string): string => {
    if (!resourceId) return '';

    // Handle patent and publication types
    if (resourceId.startsWith('patent-')) {
      const number = resourceId.replace('patent-', '');
      return `US Patent ${number}`;
    } else if (resourceId.startsWith('publication-')) {
      const number = resourceId.replace('publication-', '');
      return `US Publication ${number}`;
    } else if (resourceId.startsWith('foreign_patent-')) {
      // For foreign patents, just return the document number
      return resourceId.replace('foreign_patent-', '');
    }

    // For other types, just replace hyphen with space
    return resourceId.replace('-', ' ');
  };

  // Format date with time in local timezone (e.g., "11/20/2025 at 7:02 PM")
  const formatDateTime = (dateString: string): string => {
    if (!dateString) return 'Never';
    // Ensure the date string is treated as UTC
    // If it doesn't end with 'Z' or have a timezone offset, append 'Z'
    let normalizedDateString = dateString;
    if (!dateString.endsWith('Z') && !dateString.match(/[+-]\d{2}:\d{2}$/)) {
      normalizedDateString = dateString + 'Z';
    }
    const date = new Date(normalizedDateString);
    const dateStr = date.toLocaleDateString('en-US'); // MM/DD/YYYY
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }); // h:MM AM/PM
    return `${dateStr} at ${timeStr}`;
  };

  // Format date only in local timezone (e.g., "11/20/2025")
  const formatDate = (dateString: string): string => {
    if (!dateString) return 'Unknown';
    // Ensure the date string is treated as UTC
    let normalizedDateString = dateString;
    if (!dateString.endsWith('Z') && !dateString.match(/[+-]\d{2}:\d{2}$/)) {
      normalizedDateString = dateString + 'Z';
    }
    const date = new Date(normalizedDateString);
    return date.toLocaleDateString('en-US'); // MM/DD/YYYY
  };

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [sessionDocuments, setSessionDocuments] = useState<SessionDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<SessionDocument | null>(null);
  const [documentLimit, setDocumentLimit] = useState(50);
  const [hasMoreDocuments, setHasMoreDocuments] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isFullTextSearch, setIsFullTextSearch] = useState(false);
  const [semanticResults, setSemanticResults] = useState<any[]>([]);
  const [isSemanticSearching, setIsSemanticSearching] = useState(false);
  const [semanticSearchError, setSemanticSearchError] = useState('');
  const [hasSearchedOnce, setHasSearchedOnce] = useState(false);

  // Metadata search (fast search by filename, ID, title)
  const [metadataSearchResults, setMetadataSearchResults] = useState<SessionDocument[]>([]);
  const [isMetadataSearching, setIsMetadataSearching] = useState(false);
  const { addNotification } = useNotificationStore();
  const { startPolling } = useJobPollingStore();
  const { compactMode } = useCompactMode();

  // Debounced metadata search (when NOT in full text mode)
  useEffect(() => {
    const searchDocuments = async () => {
      // Only search if NOT in full text mode
      if (isFullTextSearch) {
        setMetadataSearchResults([]);
        setIsMetadataSearching(false);
        return;
      }

      if (!searchQuery.trim()) {
        setMetadataSearchResults([]);
        setIsMetadataSearching(false);
        return;
      }

      if (searchQuery.trim().length < 2) {
        setMetadataSearchResults([]);
        setIsMetadataSearching(false);
        return;
      }

      console.log('[DocumentsPage] Metadata search for:', searchQuery);
      setIsMetadataSearching(true);
      try {
        const results = await documentApi.searchDocuments(searchQuery, 100);
        console.log('[DocumentsPage] Metadata search results:', results);
        setMetadataSearchResults(results);
      } catch (error) {
        console.error('[DocumentsPage] Metadata search failed:', error);
        setMetadataSearchResults([]);
      } finally {
        setIsMetadataSearching(false);
      }
    };

    const timeoutId = setTimeout(searchDocuments, 1000);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, isFullTextSearch]);

  useEffect(() => {
    loadSessionDocuments();

    // Listen for job completion events from the global polling store
    // Note: Notifications are handled globally in Main.tsx, this just handles cleanup
    const handleJobComplete = (event: CustomEvent) => {
      const { jobId, docType, docId, success, error } = event.detail;
      console.log('[DocumentsPage] Job completion event received:', event.detail);
      // Format type name properly (handle foreign_patent -> Foreign Patent)
      const resourceTypeName = docType === 'foreign_patent'
        ? 'Foreign Patent'
        : docType.charAt(0).toUpperCase() + docType.slice(1);

      if (success) {
        removeLoadingDocument(docId, resourceTypeName);
        // Refresh the documents list to show the newly onboarded document
        loadSessionDocuments();
      } else {
        removeLoadingDocument(docId, resourceTypeName);

        // Remove from context
        const stored = localStorage.getItem('session_context');
        const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

        if (docType === 'patent') {
          context.current_patents = (context.current_patents || []).filter((id: string) => id !== docId);
        } else if (docType === 'publication') {
          context.current_publications = (context.current_publications || []).filter((id: string) => id !== docId);
        } else if (docType === 'application') {
          context.current_applications = (context.current_applications || []).filter((id: string) => id !== docId);
        } else if (docType === 'foreign_patent') {
          context.current_foreign_patents = (context.current_foreign_patents || []).filter((id: string) => id !== docId);
        }

        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));
      }
    };

    window.addEventListener('job-complete', handleJobComplete as EventListener);

    return () => {
      window.removeEventListener('job-complete', handleJobComplete as EventListener);
    };
  }, [addNotification]);

  const loadSessionDocuments = async (override_cache = false) => {
    setLoading(true);
    try {
      const documents = await documentApi.fetchSessionDocuments(documentLimit);
      setSessionDocuments(documents);
      // Check if there might be more documents (if we got exactly the limit, there might be more)
      setHasMoreDocuments(documents.length === documentLimit);
    } catch (error) {
      console.error('Failed to load session documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreDocuments = async () => {
    setLoadingMore(true);
    try {
      const newLimit = documentLimit + 50;
      const documents = await documentApi.fetchSessionDocuments(newLimit);
      setSessionDocuments(documents);
      setDocumentLimit(newLimit);
      // Check if there might be more documents
      setHasMoreDocuments(documents.length === newLimit);
    } catch (error) {
      console.error('Failed to load more documents:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Perform semantic (full text) search
  const performSemanticSearch = async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setSemanticResults([]);
      setSemanticSearchError('');
      return;
    }

    setHasSearchedOnce(true);
    setIsSemanticSearching(true);
    setSemanticSearchError('');

    try {
      const response = await documentApi.semanticSearchDocuments(query.trim(), 20);
      if (response.error) {
        setSemanticSearchError(response.error);
        setSemanticResults([]);
      } else {
        const results = response.results || [];
        setSemanticResults(results);
        if (response.message && results.length === 0) {
          setSemanticSearchError(response.message);
        } else {
          setSemanticSearchError('');
        }
      }
    } catch (error) {
      console.error('Semantic search failed:', error);
      setSemanticSearchError('Search failed. Please try again.');
      setSemanticResults([]);
    } finally {
      setIsSemanticSearching(false);
    }
  };

  // Handle search mode toggle
  const handleSearchModeChange = (newIsFullTextSearch: boolean) => {
    setIsFullTextSearch(newIsFullTextSearch);
    setSemanticResults([]);
    setSemanticSearchError('');
    setHasSearchedOnce(false);
  };

  const handleDelete = (doc: SessionDocument) => {
    setDocumentToDelete(doc);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!documentToDelete) return;

    console.log('[DocumentsPage] Attempting to delete document:', {
      file_type: documentToDelete.file_type,
      resource_id: documentToDelete.resource_id,
      file_name: documentToDelete.file_name
    });

    setIsDeleting(true);
    try {
      await documentApi.deleteDocument(documentToDelete.file_type, documentToDelete.resource_id);
      console.log('[DocumentsPage] Delete API call successful');
      setSessionDocuments(prev => prev.filter(d => d.resource_id !== documentToDelete.resource_id));

      // Update session_context to remove the deleted document
      try {
        const stored = localStorage.getItem('session_context');
        const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

        // Extract just the ID without type prefix
        const docId = documentToDelete.resource_id.replace(/^(patent-|publication-|application-|upload-)/, '');
        const docType = documentToDelete.file_type.toLowerCase();

        // Remove from resource_ids and resource_names
        if (context.resource_ids) {
          context.resource_ids = context.resource_ids.filter((id: string) => id !== docId && id !== documentToDelete.resource_id);
        }
        if (context.resource_names) {
          context.resource_names = context.resource_names.filter((name: string) => name !== documentToDelete.file_name);
        }
        if (context.new_resource_ids) {
          context.new_resource_ids = context.new_resource_ids.filter((id: string) => id !== docId && id !== documentToDelete.resource_id);
        }
        if (context.new_resource_names) {
          context.new_resource_names = context.new_resource_names.filter((name: string) => name !== documentToDelete.file_name);
        }

        // Remove from type-specific arrays
        if (docType === 'patent' && context.current_patents) {
          context.current_patents = context.current_patents.filter((id: string) => id !== docId);
        }
        if (docType === 'publication' && context.current_publications) {
          context.current_publications = context.current_publications.filter((id: string) => id !== docId);
        }
        if (docType === 'application' && context.current_applications) {
          context.current_applications = context.current_applications.filter((id: string) => id !== docId);
        }
        if (docType === 'foreign_patent' && context.current_foreign_patents) {
          context.current_foreign_patents = context.current_foreign_patents.filter((id: string) => id !== docId);
        }

        // Remove from extracted_resources if present
        if (context.extracted_resources) {
          context.extracted_resources = context.extracted_resources.filter((resource: any) => {
            const resourceId = resource.resource_id || resource.id || resource;
            return resourceId !== docId && resourceId !== documentToDelete.resource_id;
          });
        }

        // Clear singular references if they match the deleted document
        if (context.current_patent === docId) context.current_patent = null;
        if (context.current_publication === docId) context.current_publication = null;
        if (context.current_application === docId) context.current_application = null;

        localStorage.setItem('session_context', JSON.stringify(context));

        // Also update separate localStorage keys for new_resource_ids and new_resource_names
        try {
          const storedNewIds = localStorage.getItem('new_resource_ids');
          if (storedNewIds) {
            const newIds = JSON.parse(storedNewIds);
            if (Array.isArray(newIds)) {
              const filteredIds = newIds.filter((id: string) => id !== docId && id !== documentToDelete.resource_id);
              localStorage.setItem('new_resource_ids', JSON.stringify(filteredIds));
            }
          }

          const storedNewNames = localStorage.getItem('new_resource_names');
          if (storedNewNames) {
            const newNames = JSON.parse(storedNewNames);
            if (Array.isArray(newNames)) {
              const filteredNames = newNames.filter((name: string) => name !== documentToDelete.file_name);
              localStorage.setItem('new_resource_names', JSON.stringify(filteredNames));
            }
          }
        } catch (storageError) {
          console.error('Failed to update new_resource localStorage keys:', storageError);
        }

        window.dispatchEvent(new Event('storage-update'));
        console.log('[DocumentsPage] Updated session_context after deleting document:', documentToDelete.resource_id);
      } catch (contextError) {
        console.error('Failed to update session_context after delete:', contextError);
      }

      addNotification({
        type: 'success',
        title: 'Document Deleted',
        message: `${documentToDelete.file_name} has been removed from your session.`,
        duration: 3000,
      });
    } catch (error) {
      console.error('[DocumentsPage] Failed to delete document:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete document. Please try again.';
      addNotification({
        type: 'error',
        title: 'Delete Failed',
        message: errorMessage,
        duration: 5000,
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setDocumentToDelete(null);
    }
  };

  const pollJobStatus = (jobId: string, docType: string, docId: string) => {
    console.log('[DocumentsPage] Starting poll via global store:', { jobId, docType, docId });
    // Use global polling store that persists across navigation
    startPolling(jobId, docType, docId);
  };

  const getDocumentUrl = (doc: SessionDocument) => {
    // Get clean document ID
    let docId = doc.resource_id.replace(/^(patent-|publication-|application-)/, '');

    // For patents/publications, ensure US prefix
    if (['patent', 'publication', 'application'].includes(doc.file_type)) {
      const number = docId.replace(/,/g, '').replace(/\//g, '');
      // If it's a publication (starts with 20), use as-is with US prefix
      if (number.startsWith('20') && number.length >= 11) {
        return `/US${number}`;
      }
      // For patents (7-8 digits), add US prefix
      return `/US${number}`;
    }

    return null;
  };

  const handleOpenInNewTab = (doc: SessionDocument) => {
    const url = getDocumentUrl(doc);
    if (url) {
      window.open(url, '_blank');
    }
  };

  const addToActiveDocuments = async (doc: SessionDocument) => {
    try {
      // Handle uploads differently - add directly to session context
      if (doc.file_type === 'upload') {
        const stored = localStorage.getItem('session_context');
        const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

        const resourceNames = context.resource_names || [];
        const resourceIds = context.resource_ids || [];

        // Check if already added
        if (resourceNames.includes(doc.file_name)) {
          addNotification({
            type: 'info',
            title: 'Already Added',
            message: `${doc.file_name} is already in your active documents.`,
            duration: 3000,
          });
          return;
        }

        // Add to context
        resourceNames.push(doc.file_name);
        resourceIds.push(doc.resource_id);

        context.resource_names = resourceNames;
        context.resource_ids = resourceIds;

        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));

        addNotification({
          type: 'success',
          title: 'Added to Active Documents',
          message: `${doc.file_name} has been added to your session.`,
          duration: 3000,
        });

        return;
      }

      // Only handle patent, publication, and application types through the API
      if (!['patent', 'publication', 'application'].includes(doc.file_type)) {
        addNotification({
          type: 'info',
          title: 'Not Supported',
          message: 'This document type cannot be added to active documents.',
          duration: 5000,
        });
        return;
      }

      // Clean the document ID - remove prefixes like "patent-", "publication-", "application-"
      let cleanedId = doc.resource_id
        .replace(/^(patent-|publication-|application-)/, '')
        .replace(/,/g, '')
        .replace(/\//g, '')
        .replace(/^US/i, '')
        .replace(/[A-Z]\d*$/i, '');

      // Check if already in context
      const stored = localStorage.getItem('session_context');
      const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

      let contextKey: string;
      if (doc.file_type === 'patent') {
        contextKey = 'current_patents';
      } else if (doc.file_type === 'publication') {
        contextKey = 'current_publications';
      } else if (doc.file_type === 'application') {
        contextKey = 'current_applications';
      } else {
        return;
      }

      const currentDocs = Array.isArray(context[contextKey]) ? context[contextKey] : [];
      if (currentDocs.includes(cleanedId)) {
        addNotification({
          type: 'info',
          title: 'Already Added',
          message: `${cleanedId} is already in your active documents.`,
          duration: 3000,
        });
        return;
      }

      // Call the API to fetch and load the document
      const response = await documentApi.fetchAndLoadDocument(doc.file_type, cleanedId);

      if (response?.job_id) {
        // Add to session context immediately
        context[contextKey] = [...currentDocs, cleanedId];
        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));

        // Show processing notification (will auto-dismiss for non-success)
        const resourceTypeName = doc.file_type.charAt(0).toUpperCase() + doc.file_type.slice(1);
        addNotification({
          type: 'info',
          title: `Loading ${resourceTypeName}`,
          message: `Processing ${cleanedId}...`,
          duration: 60000,
        });

        // Add to loading state
        addLoadingDocument(cleanedId, resourceTypeName, cleanedId);

        // Start polling for job status
        pollJobStatus(response.job_id, doc.file_type, cleanedId);
      }
    } catch (error) {
      console.error('Failed to add to active documents:', error);
      addNotification({
        type: 'error',
        title: 'Failed to Add',
        message: 'Failed to add document to active documents. Please try again.',
        duration: 5000,
      });
    }
  };

  // Convert session documents to display format
  const allDocuments = sessionDocuments
    .filter(doc => {
      // Filter out invalid documents (must have file_name and resource_id)
      // Also filter out test/corrupted entries
      return doc.file_name &&
             doc.resource_id &&
             doc.file_type &&
             doc.file_name !== 'Add to Active' && // Filter out corrupted entries
             doc.resource_id !== '101'; // Filter out test entries
    })
    .map(doc => {
      // Use title if available (for patents/publications), otherwise use file_name (for uploads)
      let displayTitle = doc.title || doc.file_name;
      // Remove type prefixes from title for cleaner display
      if (displayTitle) {
        displayTitle = displayTitle.replace(/^(patent-|publication-|application-|foreign_patent[\s-])/gi, '');
      }
      return {
        ...doc,
        id: doc.resource_id,
        title: displayTitle,
        type: doc.file_type,
        status: 'Loaded',
        lastAccessed: doc.last_accessed_date ? formatDateTime(doc.last_accessed_date) : 'Never',
        onboardedDate: doc.create_date ? formatDate(doc.create_date) : 'Unknown',
        lastAccessedTimestamp: doc.last_accessed_date ? new Date(doc.last_accessed_date + (doc.last_accessed_date.endsWith('Z') || doc.last_accessed_date.match(/[+-]\d{2}:\d{2}$/) ? '' : 'Z')).getTime() : 0,
      };
    });

  // Determine documents to display based on search mode
  // Note: Backend already returns results sorted by relevance (distance field, lower = more relevant)
  const documentsToDisplay = isFullTextSearch && semanticResults.length > 0
    ? semanticResults.map(doc => {
        // Use title if available (for patents/publications), otherwise use file_name (for uploads)
        let displayTitle = doc.title || doc.file_name;
        // Remove type prefixes from title for cleaner display
        if (displayTitle) {
          displayTitle = displayTitle.replace(/^(patent-|publication-|application-|foreign_patent[\s-])/gi, '');
        }
        return {
          ...doc,
          id: doc.resource_id,
          title: displayTitle,
          type: doc.file_type,
          status: 'Loaded',
          lastAccessed: doc.last_accessed_date ? formatDateTime(doc.last_accessed_date) : 'Never',
          onboardedDate: doc.create_date ? formatDate(doc.create_date) : 'Unknown',
          lastAccessedTimestamp: doc.last_accessed_date ? new Date(doc.last_accessed_date + (doc.last_accessed_date.endsWith('Z') || doc.last_accessed_date.match(/[+-]\d{2}:\d{2}$/) ? '' : 'Z')).getTime() : 0,
          _sourceDoc: doc, // Keep reference to original document
        };
      })
    : allDocuments;

  // Helper function to safely get source document
  const getSourceDoc = (doc: any): SessionDocument | undefined => {
    // If doc has _sourceDoc (from semantic search), use it
    if (doc._sourceDoc) {
      return doc._sourceDoc;
    }
    // Otherwise try to find in sessionDocuments
    return sessionDocuments.find(d => d.resource_id === doc.id);
  };

  // Filter documents based on search and filter criteria
  const filteredDocuments = (() => {
    // If metadata search is active and has results, use those
    if (!isFullTextSearch && searchQuery.trim().length >= 2 && metadataSearchResults.length > 0) {
      // Transform metadata search results same as semantic search results
      return metadataSearchResults
        .map(doc => {
          // Use title if available (for patents/publications), otherwise use file_name (for uploads)
          let displayTitle = doc.title || doc.file_name;
          // Remove type prefixes from title for cleaner display
          if (displayTitle) {
            displayTitle = displayTitle.replace(/^(patent-|publication-|application-|foreign_patent[\s-])/gi, '');
          }
          return {
            ...doc,
            id: doc.resource_id,
            title: displayTitle,
            type: doc.file_type,
            status: 'Loaded',
            lastAccessed: doc.last_accessed_date ? formatDateTime(doc.last_accessed_date) : 'Never',
            onboardedDate: doc.create_date ? formatDate(doc.create_date) : 'Unknown',
            lastAccessedTimestamp: doc.last_accessed_date ? new Date(doc.last_accessed_date + (doc.last_accessed_date.endsWith('Z') || doc.last_accessed_date.match(/[+-]\d{2}:\d{2}$/) ? '' : 'Z')).getTime() : 0,
            _sourceDoc: doc, // Keep reference to original document
          };
        })
        .filter(doc => {
          const matchesFilter = filterType === 'all' || (doc.file_type || '').toLowerCase() === filterType.toLowerCase();
          return matchesFilter;
        });
    }

    // Otherwise use regular filtering
    return documentsToDisplay.filter(doc => {
      // For semantic search, don't filter by text search since results are already filtered
      const matchesSearch = isFullTextSearch ? true :
        !searchQuery.trim() ||
        (doc.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (doc.resource_id || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter = filterType === 'all' || (doc.type || '').toLowerCase() === filterType.toLowerCase();
      return matchesSearch && matchesFilter;
    });
  })()
    // Sort by most recently accessed (unless in semantic/metadata search mode where relevance matters)
    .sort((a, b) => {
      if (isFullTextSearch && semanticResults.length > 0) {
        // Keep semantic search results in relevance order
        return 0;
      }
      if (!isFullTextSearch && metadataSearchResults.length > 0) {
        // Keep metadata search results in relevance order (backend sorted)
        return 0;
      }
      // Sort by last accessed timestamp, most recent first
      return (b.lastAccessedTimestamp || 0) - (a.lastAccessedTimestamp || 0);
    });

  // Show minimized view when document viewer is open
  if (isDocumentViewerOpen) {
    return (
      <div className={`flex flex-col h-full bg-white border-r border-gray-200 transition-all duration-300 overflow-hidden ${isCollapsed ? 'w-12' : 'w-[320px]'}`}>
        {/* Collapse Toggle */}
        <div className="border-b border-gray-200 px-3 py-4 flex items-center justify-between">
          {!isCollapsed && <span className="text-sm text-gray-600">Documents</span>}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>

        {!isCollapsed && (
          <>
            {/* Compact Search */}
            <div className="border-b border-gray-200 px-3 py-3">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-9 text-sm"
                />
                {(isSemanticSearching || isMetadataSearching) && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <Loader2 className="w-3 h-3 animate-spin text-blue-600" />
                  </div>
                )}
              </div>
              {/* Results count for compact mode */}
              {!isFullTextSearch && searchQuery.trim().length >= 2 && !isMetadataSearching && (
                <div className="text-xs text-gray-600 mt-1">
                  {metadataSearchResults.length > 0 ? (
                    <span className="text-green-700">
                      ✓ {metadataSearchResults.length} match{metadataSearchResults.length !== 1 ? 'es' : ''}
                    </span>
                  ) : (
                    <span className="text-amber-700">No matches</span>
                  )}
                </div>
              )}
            </div>

            {/* Compact Document List */}
            <div className="flex-1 overflow-hidden w-full max-w-[320px]">
              <ScrollArea className="h-full" style={{ width: '100%', maxWidth: '100%' }}>
                <div className="p-2 space-y-1" style={{ width: '100%', maxWidth: '330px', minWidth: '0' }}>
                {loading ? (
                  <div className="text-center text-sm text-gray-500 py-4">Loading documents...</div>
                ) : filteredDocuments.length === 0 ? (
                  <div className="text-center text-sm text-gray-500 py-4">No documents found</div>
                ) : (
                  filteredDocuments.map((doc) => (
                    <button
                      key={doc.id}
                      className="w-full text-left p-3 rounded-md hover:bg-gray-100 transition-colors border border-transparent hover:border-gray-200 block"
                      onClick={() => onDocumentSelect(doc)}
                    >
                      <div className="flex items-start gap-2 mb-2 w-full">
                        <FileText className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-gray-900 font-medium mb-1 truncate" title={doc.title}>
                            {doc.title}
                          </div>
                          <div className="text-xs text-gray-500 truncate" title={formatResourceId(doc.resource_id)}>
                            {formatResourceId(doc.resource_id)}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
              </ScrollArea>
            </div>
          </>
        )}
      </div>
    );
  }

  // Show full view when document viewer is not open
  return (
    <div className="flex flex-col h-full bg-white overflow-hidden">
      {/* Toolbar */}
      <div className={`flex-shrink-0 border-b border-gray-200 bg-white ${compactMode ? 'px-4 py-2' : 'px-6 py-4'}`}>
        <div className={`flex items-center justify-between gap-4 ${compactMode ? 'mb-2' : 'mb-4'}`}>
          <div className="flex-1 flex gap-2">
            <div className="relative flex-1 max-w-2xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <Input
                placeholder={isFullTextSearch ?
                  "Find relevant documents by searching their full text content..." :
                  "Search documents by filename, type, ID..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (isFullTextSearch) {
                      performSemanticSearch(searchQuery);
                    }
                    // Metadata search is already triggered by debounce
                  }
                }}
                className={`pl-9 ${isFullTextSearch ? 'border-blue-400 bg-blue-50' : ''}`}
                disabled={isSemanticSearching || isMetadataSearching}
              />
              {searchQuery && !isSemanticSearching && !isMetadataSearching && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSemanticResults([]);
                    setSemanticSearchError('');
                    setMetadataSearchResults([]);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 pointer-events-auto"
                  type="button"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Search Button (for both metadata and full-text search) */}
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (isFullTextSearch) {
                  performSemanticSearch(searchQuery);
                } else {
                  // Force metadata search by adding/removing space to trigger debounce
                  if (searchQuery.trim().length >= 2) {
                    setSearchQuery(searchQuery + ' ');
                    setTimeout(() => setSearchQuery(searchQuery.trim()), 0);
                  }
                }
              }}
              disabled={(isSemanticSearching || isMetadataSearching) || searchQuery.trim().length < 2}
              className="gap-2"
            >
              {(isSemanticSearching || isMetadataSearching) ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              Search
            </Button>

            {/* Full Text Search Toggle */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Full Text Search
              </label>
              <button
                onClick={() => handleSearchModeChange(!isFullTextSearch)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-200 ${
                  isFullTextSearch ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-all duration-200 ${
                    isFullTextSearch ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Metadata Search Results Info */}
          {!isFullTextSearch && searchQuery.trim().length >= 2 && !isMetadataSearching && (
            <div className="text-xs text-gray-600">
              {metadataSearchResults.length > 0 ? (
                <span className="text-green-700">
                  ✓ Found {metadataSearchResults.length} match{metadataSearchResults.length !== 1 ? 'es' : ''}
                </span>
              ) : (
                <span className="text-amber-700">No documents found matching "{searchQuery}"</span>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            {onAddDocuments && (
              <Button
                onClick={onAddDocuments}
                className="gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Document
              </Button>
            )}

            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Documents</SelectItem>
                <SelectItem value="patent">Patents</SelectItem>
                <SelectItem value="publication">Publications</SelectItem>
                <SelectItem value="upload">Uploads</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-1">
              <Button
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-8 w-8"
                onClick={() => setViewMode('grid')}
              >
                <Grid3x3 className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-8 w-8"
                onClick={() => setViewMode('list')}
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 text-sm">
          {searchQuery && isFullTextSearch ? (
            <span className={`${semanticResults.length > 0 ? 'text-blue-600' : 'text-gray-600'}`}>
              {isSemanticSearching ? (
                "Searching..."
              ) : semanticSearchError ? (
                <span className="text-red-600">{semanticSearchError}</span>
              ) : semanticResults.length > 0 ? (
                <>
                  Found {semanticResults.length} document{semanticResults.length !== 1 ? 's' : ''} matching "{searchQuery}"
                  <span className="ml-2 text-xs text-gray-500">(sorted by relevance)</span>
                </>
              ) : hasSearchedOnce ? (
                `No documents found matching "${searchQuery}"`
              ) : (
                `Press Enter or click Search to find documents matching "${searchQuery}"`
              )}
            </span>
          ) : searchQuery ? (
            <span className="text-gray-600">
              Found {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''} matching "{searchQuery}"
            </span>
          ) : (
            <>
              <span className="text-gray-600">{filteredDocuments.length} documents</span>
              <span className="text-gray-300">•</span>
              <span className="text-gray-600">Last updated: Today</span>
            </>
          )}
        </div>
      </div>

      {/* Documents Grid/List */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          <div className={compactMode ? 'p-3' : 'p-6'}>
          {loading ? (
            <div className={`text-center ${compactMode ? 'py-8' : 'py-12'}`}>
              <FileText className={`${compactMode ? 'w-8 h-8 mb-2' : 'w-12 h-12 mb-3'} text-gray-300 mx-auto`} />
              <p className={`text-gray-500 ${compactMode ? 'text-sm' : ''}`}>Loading documents...</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className={`grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 ${compactMode ? 'gap-2' : 'gap-4'}`}>
              {filteredDocuments.map((doc) => (
                <Card
                  key={doc.id}
                  className="cursor-pointer hover:shadow-md transition-shadow group"
                  onClick={() => onDocumentSelect(doc)}
                >
                  <CardHeader className="pb-1">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <Badge variant="outline" className="bg-gray-100 text-gray-700 border-gray-200">
                        {doc.type}
                      </Badge>
                      <div className="flex items-start justify-between gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            const sourceDoc = getSourceDoc(doc);
                            if (sourceDoc) addToActiveDocuments(sourceDoc);
                          }}
                          className="h-7 text-xs gap-1"
                        >
                          <Plus className="w-3 h-3" />
                          Add to Active
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white border border-gray-200 shadow-lg">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                const sourceDoc = getSourceDoc(doc);
                                if (sourceDoc) addToActiveDocuments(sourceDoc);
                              }}
                              className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Add to Active Documents
                            </DropdownMenuItem>
                            {(() => {
                              const sourceDoc = getSourceDoc(doc);
                              return sourceDoc && getDocumentUrl(sourceDoc) && (
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (sourceDoc) handleOpenInNewTab(sourceDoc);
                                  }}
                                  className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                                >
                                  <ExternalLink className="w-4 h-4 mr-2" />
                                  Open in New Tab
                                </DropdownMenuItem>
                              );
                            })()}
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                onDocumentSelect(doc);
                              }}
                              className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View Document
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                const sourceDoc = getSourceDoc(doc);
                                if (sourceDoc) handleDelete(sourceDoc);
                              }}
                              className="text-red-600 hover:bg-gray-100 cursor-pointer"
                              disabled={isDeleting}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    <CardTitle className="text-base leading-tight line-clamp-2 break-words" title={doc.title}>
                      {doc.title}
                    </CardTitle>
                    <CardDescription className="space-y-1 mt-2">
                      <div className="text-xs">{formatResourceId(doc.resource_id)}</div>
                      <div className="text-xs">Type: {doc.type}</div>
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {doc.summary && (
                      <p className="text-sm text-gray-600 line-clamp-3">{doc.summary}</p>
                    )}

                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      {doc.onboardedDate && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          Onboarded: {doc.onboardedDate}
                        </div>
                      )}
                      {doc.file_size && (
                        <div className="flex items-center gap-1">
                          <FileText className="w-3.5 h-3.5" />
                          {(doc.file_size / 1024).toFixed(1)} KB
                        </div>
                      )}
                    </div>

                    {doc.lastAccessed && doc.lastAccessed !== 'Never' && (
                      <div className="pt-2 border-t border-gray-100">
                        <div className="text-xs text-gray-500">
                          Last accessed: {doc.lastAccessed}
                        </div>
                      </div>
                    )}

                    {/* Semantic Search Preview */}
                    {isFullTextSearch && doc.chunk_preview && (
                      <div className="pt-2 border-t border-blue-200 bg-blue-50 rounded-lg p-2">
                        <div className="flex items-start gap-2">
                          <div className="flex flex-col gap-1">
                            <Badge className="bg-blue-600 text-white text-xs">MATCH</Badge>
                            {doc._sourceDoc?.num_chunks !== undefined && doc._sourceDoc?.total_chunks !== undefined && (
                              <Badge className="bg-green-600 text-white text-xs" title="Relevant sections / Total sections">
                                {doc._sourceDoc.num_chunks}/{doc._sourceDoc.total_chunks}
                              </Badge>
                            )}
                            {doc._sourceDoc?.tf_score !== undefined && (
                              <Badge className="bg-purple-600 text-white text-xs" title="Relevance density (TF score)">
                                {(doc._sourceDoc.tf_score * 100).toFixed(0)}%
                              </Badge>
                            )}
                            {doc._sourceDoc?.distance !== undefined && (
                              <Badge className="bg-gray-600 text-white text-xs" title="Best match quality">
                                {(1 - doc._sourceDoc.distance).toFixed(3)}
                              </Badge>
                            )}
                          </div>
                          <div className="flex-1">
                            {doc.section_title && (
                              <div className="text-xs font-medium text-blue-900 mb-1 prose prose-sm max-w-none [&>p]:inline">
                                <Markdown>{fixMarkdownFormatting(doc.section_title)}</Markdown>
                              </div>
                            )}
                            <div className="text-xs text-blue-900 leading-relaxed line-clamp-3 prose prose-sm max-w-none [&>p]:inline">
                              <Markdown>{fixMarkdownFormatting(doc.chunk_preview)}</Markdown>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer group"
                  onClick={() => onDocumentSelect(doc)}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6 text-blue-600" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-gray-900 font-semibold truncate" title={doc.title}>{doc.title}</h3>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                const sourceDoc = getSourceDoc(doc);
                                if (sourceDoc) addToActiveDocuments(sourceDoc);
                              }}
                              className="h-7 text-xs gap-1 flex-shrink-0"
                            >
                              <Plus className="w-3 h-3" />
                              Add to Active
                            </Button>
                          </div>
                          <div className="flex items-center gap-3 text-sm text-gray-500">
                            <span>{formatResourceId(doc.resource_id)}</span>
                            <span>•</span>
                            <Badge variant="outline" className="bg-gray-100 text-gray-700 border-gray-200 text-xs">
                              {doc.type}
                            </Badge>
                          </div>
                        </div>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white border border-gray-200 shadow-lg">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                const sourceDoc = getSourceDoc(doc);
                                if (sourceDoc) addToActiveDocuments(sourceDoc);
                              }}
                              className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Add to Active Documents
                            </DropdownMenuItem>
                            {(() => {
                              const sourceDoc = getSourceDoc(doc);
                              return sourceDoc && getDocumentUrl(sourceDoc) && (
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (sourceDoc) handleOpenInNewTab(sourceDoc);
                                  }}
                                  className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                                >
                                  <ExternalLink className="w-4 h-4 mr-2" />
                                  Open in New Tab
                                </DropdownMenuItem>
                              );
                            })()}
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                onDocumentSelect(doc);
                              }}
                              className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                const sourceDoc = getSourceDoc(doc);
                                if (sourceDoc) handleDelete(sourceDoc);
                              }}
                              className="text-red-600 hover:bg-gray-100 cursor-pointer"
                              disabled={isDeleting}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {doc.summary && (
                        <p className="text-sm text-gray-600 line-clamp-2 mb-3">{doc.summary}</p>
                      )}

                      <div className="flex items-center gap-6 text-xs text-gray-500">
                        {doc.onboardedDate && (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            Onboarded: {doc.onboardedDate}
                          </div>
                        )}
                        {doc.file_size && (
                          <div className="flex items-center gap-1">
                            <FileText className="w-3.5 h-3.5" />
                            {(doc.file_size / 1024).toFixed(1)} KB
                          </div>
                        )}
                        {doc.lastAccessed && doc.lastAccessed !== 'Never' && (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            Accessed: {doc.lastAccessed}
                          </div>
                        )}
                      </div>

                      {/* Semantic Search Preview */}
                      {isFullTextSearch && doc.chunk_preview && (
                        <div className="mt-3 pt-3 border-t border-blue-200 bg-blue-50 rounded-lg p-3">
                          <div className="flex items-start gap-2">
                            <div className="flex flex-col gap-1">
                              <Badge className="bg-blue-600 text-white text-xs">MATCH</Badge>
                              {doc._sourceDoc?.num_chunks !== undefined && doc._sourceDoc?.total_chunks !== undefined && (
                                <Badge className="bg-green-600 text-white text-xs" title="Relevant sections / Total sections">
                                  {doc._sourceDoc.num_chunks}/{doc._sourceDoc.total_chunks}
                                </Badge>
                              )}
                              {doc._sourceDoc?.tf_score !== undefined && (
                                <Badge className="bg-purple-600 text-white text-xs" title="Relevance density (TF score)">
                                  {(doc._sourceDoc.tf_score * 100).toFixed(0)}%
                                </Badge>
                              )}
                              {doc._sourceDoc?.distance !== undefined && (
                                <Badge className="bg-gray-600 text-white text-xs" title="Best match quality">
                                  {(1 - doc._sourceDoc.distance).toFixed(3)}
                                </Badge>
                              )}
                            </div>
                            <div className="flex-1">
                              {doc.section_title && (
                                <div className="text-xs font-medium text-blue-900 mb-1 prose prose-sm max-w-none [&>p]:inline">
                                  <Markdown>{fixMarkdownFormatting(doc.section_title)}</Markdown>
                                </div>
                              )}
                              <div className="text-xs text-blue-900 leading-relaxed prose prose-sm max-w-none [&>p]:inline">
                                <Markdown>{fixMarkdownFormatting(doc.chunk_preview)}</Markdown>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Load More Button */}
          {!loading && hasMoreDocuments && filteredDocuments.length > 0 && (
            <div className="flex justify-center mt-6">
              <Button
                onClick={loadMoreDocuments}
                disabled={loadingMore}
                variant="outline"
                className="gap-2"
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    Load More
                    <ChevronDown className="w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          )}

          {!loading && filteredDocuments.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No documents found</p>
              <p className="text-sm text-gray-400 mt-1">Try adjusting your search or filters</p>
            </div>
          )}
          </div>
        </ScrollArea>
      </div>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <span className="font-semibold">{documentToDelete?.file_name}</span>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
